import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getJournalEntries, createJournalEntry, deleteJournalEntry, getAccounts } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash, Notebook, Equals, ArrowDown, ArrowUp } from '@phosphor-icons/react';

export default function JournalEntriesPage() {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    memo: '',
    lines: [
      { _uid: 'line-1', account_id: '', debit: '', credit: '', description: '' },
      { _uid: 'line-2', account_id: '', debit: '', credit: '', description: '' }
    ]
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jeRes, acctRes] = await Promise.all([getJournalEntries(), getAccounts()]);
      setEntries(jeRes.data.entries);
      setAccounts(acctRes.data.accounts);
    } catch (error) {
      toast.error('Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  };

  const addLine = () => {
    setForm({ ...form, lines: [...form.lines, { _uid: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, account_id: '', debit: '', credit: '', description: '' }] });
  };

  const removeLine = (idx) => {
    if (form.lines.length <= 2) return;
    setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) });
  };

  const updateLine = (idx, field, value) => {
    const updated = [...form.lines];
    updated[idx] = { ...updated[idx], [field]: value };
    // Auto-clear opposite when entering debit/credit
    if (field === 'debit' && value) updated[idx].credit = '';
    if (field === 'credit' && value) updated[idx].debit = '';
    setForm({ ...form, lines: updated });
  };

  const totalDebit = form.lines.reduce((a, l) => a + (parseFloat(l.debit) || 0), 0);
  const totalCredit = form.lines.reduce((a, l) => a + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isBalanced) { toast.error('Debits must equal credits'); return; }
    try {
      const acctMap = Object.fromEntries(accounts.map(a => [a.account_id, `${a.code} - ${a.name}`]));
      await createJournalEntry({
        date: form.date,
        memo: form.memo,
        lines: form.lines.filter(l => l.account_id).map(l => ({
          account_id: l.account_id,
          account_name: acctMap[l.account_id] || l.account_id,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          description: l.description
        }))
      });
      toast.success('Journal entry posted');
      setIsDialogOpen(false);
      setForm({ date: new Date().toISOString().split('T')[0], memo: '', lines: [{ _uid: 'line-1', account_id: '', debit: '', credit: '', description: '' }, { _uid: 'line-2', account_id: '', debit: '', credit: '', description: '' }] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create journal entry');
    }
  };

  const handleDelete = async (jeId) => {
    if (!window.confirm('Delete this journal entry?')) return;
    try {
      await deleteJournalEntry(jeId);
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (<Layout title="Journal Entries"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div></div></Layout>);
  }

  return (
    <Layout title="Journal Entries">
      <div data-testid="journal-entries-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-sm text-zinc-600">Double-entry journal entries. Debits must equal credits.</p>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-zinc-950 text-white rounded-sm" data-testid="add-je-button">
                <Plus size={16} className="mr-2" /> New Journal Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="je-dialog">
              <DialogHeader><DialogTitle>Create Journal Entry</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Date</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className="rounded-sm" data-testid="je-date" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Memo</Label>
                    <Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} required placeholder="Description of entry" className="rounded-sm" data-testid="je-memo" />
                  </div>
                </div>

                {/* Lines */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Line Items</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addLine} className="rounded-sm text-xs">
                      <Plus size={12} className="mr-1" /> Add Line
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 text-xs text-zinc-500 uppercase tracking-widest px-1">
                      <div className="col-span-5">Account</div>
                      <div className="col-span-2 text-right">Debit</div>
                      <div className="col-span-2 text-right">Credit</div>
                      <div className="col-span-2">Note</div>
                      <div className="col-span-1"></div>
                    </div>
                    {form.lines.map((line, i) => (
                      <div key={line._uid} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-5">
                          <Select value={line.account_id} onValueChange={(v) => updateLine(i, 'account_id', v)}>
                            <SelectTrigger className="rounded-sm h-9 text-xs"><SelectValue placeholder="Account" /></SelectTrigger>
                            <SelectContent>
                              {accounts.map(a => <SelectItem key={a.account_id} value={a.account_id}>{a.code} - {a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Input type="number" step="0.01" value={line.debit} onChange={(e) => updateLine(i, 'debit', e.target.value)} placeholder="0.00" className="rounded-sm h-9 text-xs font-mono text-right" />
                        </div>
                        <div className="col-span-2">
                          <Input type="number" step="0.01" value={line.credit} onChange={(e) => updateLine(i, 'credit', e.target.value)} placeholder="0.00" className="rounded-sm h-9 text-xs font-mono text-right" />
                        </div>
                        <div className="col-span-2">
                          <Input value={line.description} onChange={(e) => updateLine(i, 'description', e.target.value)} placeholder="Note" className="rounded-sm h-9 text-xs" />
                        </div>
                        <div className="col-span-1">
                          {form.lines.length > 2 && (
                            <button type="button" onClick={() => removeLine(i)} className="p-1 text-red-400 hover:text-red-600"><Trash size={14} /></button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="grid grid-cols-12 gap-2 items-center pt-2 border-t border-zinc-200">
                    <div className="col-span-5 text-right text-xs uppercase tracking-widest text-zinc-500">Totals</div>
                    <div className={`col-span-2 text-right font-mono text-sm font-medium ${isBalanced ? 'text-emerald-600' : 'text-zinc-900'}`}>
                      ${totalDebit.toFixed(2)}
                    </div>
                    <div className={`col-span-2 text-right font-mono text-sm font-medium ${isBalanced ? 'text-emerald-600' : 'text-zinc-900'}`}>
                      ${totalCredit.toFixed(2)}
                    </div>
                    <div className="col-span-3 flex items-center gap-1">
                      {isBalanced ? (
                        <span className="text-xs text-emerald-600 flex items-center gap-1"><Equals size={12} /> Balanced</span>
                      ) : totalDebit > 0 || totalCredit > 0 ? (
                        <span className="text-xs text-red-600">Off by ${Math.abs(totalDebit - totalCredit).toFixed(2)}</span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={!isBalanced} className="bg-zinc-950 text-white rounded-sm" data-testid="post-je-button">Post Entry</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Entries List */}
        {entries.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
            <Notebook size={48} className="mx-auto mb-4 text-zinc-300" />
            <p className="font-medium mb-1">No journal entries yet</p>
            <p className="text-sm text-zinc-500">Create your first double-entry journal entry.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((je) => (
              <div key={je.je_id} className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid={`je-card-${je.je_id}`}>
                <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-sm">{je.date}</span>
                    <span className="font-medium text-sm">{je.memo}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm">${je.total_debit?.toFixed(2)}</span>
                    <button onClick={() => handleDelete(je.je_id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-sm"><Trash size={14} /></button>
                  </div>
                </div>
                <div className="px-6 py-3">
                  {je.lines?.map((line, i) => (
                    <div key={i} className="flex items-center gap-4 py-1.5 text-sm">
                      {line.debit > 0 ? (
                        <>
                          <ArrowDown size={14} className="text-emerald-500" />
                          <span className="font-mono w-24 text-right text-emerald-600">${line.debit.toFixed(2)}</span>
                          <span className="w-24"></span>
                        </>
                      ) : (
                        <>
                          <ArrowUp size={14} className="text-blue-500" />
                          <span className="w-24"></span>
                          <span className="font-mono w-24 text-right text-blue-600">${line.credit.toFixed(2)}</span>
                        </>
                      )}
                      <span className="font-medium">{line.account_name || line.account_id}</span>
                      {line.description && <span className="text-zinc-500 text-xs">— {line.description}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
